import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'

interface ProtectedRouteProps {
    children: React.ReactNode
    allowedRoles?: ('Staff' | 'Surveyor' | 'Admin' | 'Reviewer' | 'FieldSurveyor')[]
}

/**
 * Route Guard [FE.0.3]
 * Enforces authentication and RBAC using memory-only viewer context.
 *
 * Spinner only when we have neither a session nor a finished initial load.
 * A live session must not be blocked by a stuck isLoading / delayed
 * viewerContext — that left authenticated smoke on a blank spinner at /dashboard.
 */
export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
    const { session, viewerContext, isLoading } = useAuth()
    const location = useLocation()

    // Bootstrapping with no session yet
    if (isLoading && !session) {
        return (
            <div className="min-h-screen bg-neutral-900 flex items-center justify-center" data-testid="auth-boot-spinner">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
        )
    }

    // 1. Check authentication
    if (!session) {
        return <Navigate to="/login" state={{ from: location }} replace />
    }

    // 2. Check Role-Based Access (if required)
    if (allowedRoles && viewerContext && !allowedRoles.includes(viewerContext.role)) {
        const fallback = viewerContext.role === 'FieldSurveyor' ? '/today' : '/dashboard'
        return <Navigate to={fallback} replace />
    }

    // 3. Session exists — render protected tree even if viewerContext is briefly null
    // (role gates that need viewerContext handle their own fallbacks).
    return <>{children}</>
}
